import {Controller} from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import {getPendingPushUrl, clearPendingPushUrl} from "../services/onesignal_storage"

/**
 * Push Notification Redirect Controller
 *
 * Bridges Flutter push notification taps with Rails/Turbo navigation.
 *
 * Two entry points, covering both app states:
 *
 * 1. WARM START (app already open in background)
 *    Flutter calls, from its OneSignal notification-opened handler:
 *      webViewController.evaluateJavascript("window.handlePushNotification('${url}')")
 *    → Turbo.visit() navigates to the target screen immediately.
 *
 * 2. COLD START (app was killed, launched from notification)
 *    Flutter cannot call evaluateJavascript before the WebView loads.
 *    Instead, Flutter stores the URL during onLoadStop of the initial page:
 *      webViewController.evaluateJavascript(
 *        "localStorage.setItem('${PUSH_PENDING_URL_KEY}', '${url}')"
 *      )
 *    Then reloads (or waits for the next page). On connect this controller
 *    reads that key, clears it, and navigates once.
 *
 * The `url` value is the absolute URL present in `additionalData.url`
 * of the OneSignal notification (set by PushMessage#apply_absolute_notification_url!).
 */
export default class extends Controller {
    connect() {
        // Expose global entry point for Flutter (warm start & foreground).
        window.handlePushNotification = (url) => this.navigateTo(url)

        // Cold-start: process a URL that Flutter stored before this page loaded.
        this.processPendingUrl()
    }

    disconnect() {
        // Clean up so stale closures don't linger if the element is removed.
        if (window.handlePushNotification === this._handler) {
            delete window.handlePushNotification
        }
    }

    // ─── Private ───────────────────────────────────────────────────────────────

    processPendingUrl() {
        const url = getPendingPushUrl()
        if (!url) return

        clearPendingPushUrl()
        // Defer slightly so the current page finishes rendering before navigating.
        setTimeout(() => this.navigateTo(url), 150)
    }

    navigateTo(url) {
        if (!url) return

        try {
            const parsed = new URL(url, window.location.origin)

            // Ignore cross-origin URLs for safety.
            if (parsed.origin !== window.location.origin) return

            const target = parsed.pathname + parsed.search + parsed.hash

            // Already on the target page – nothing to do.
            if (target === window.location.pathname + window.location.search) return

            Turbo.visit(target)
        } catch {
            // Silently ignore malformed URLs.
        }
    }
}
